import { Button, TableCell, TableRow, TOUCH } from "@nymbl/nos2-rules";
import { ChevronRight } from "lucide-react";
import { GridCell } from "@/components/time/GridCell";
import {
  GridTaskInfo,
  GroupPlan,
  HeldComments,
  heldInRow,
  type Pending,
} from "@/components/time/GridTaskInfo";
import type { IsoDate } from "@/lib/time/dates";
import type { GridCell as GridCellModel, GridGroup, GridRow } from "@/lib/time/grid";
import { cellLabel } from "@/lib/time/labels";
import { formatHours } from "@/lib/time/rules";
import { cn } from "@/lib/utils";

/** Today's column tint (mockup: a faint accent surface). */
export const TODAY_TINT = "bg-info-bg/60";

/**
 * GridCell's key: a refetch (saved hours), a new held value, or a reset after a write that
 * may have saved each remount it, so no stale draft survives.
 */
export function cellKey(cell: GridCellModel, p: Pending | undefined, reset: number): string {
  return `${cell.key}:${cell.hours}:${p?.raw ?? ""}:${reset}`;
}

export interface GridRowHandlers {
  pending: Record<string, Pending>;
  resets: Record<string, number>;
  saving: boolean;
  favoritePending: boolean;
  onToggleFavorite: (row: GridRow) => void;
  onCommit: (row: GridRow, cell: GridCellModel, raw: string, comment: string | null) => void;
  /** A cell with several entries: Entries, filtered to that task and day. */
  onOpenEntries: (row: GridRow, cell: GridCellModel) => void;
  /** Projects collapsed in this browser (group keys). */
  collapsed: Set<string>;
  onToggleGroup: (key: string) => void;
  /** Expand all or Collapse all over these project keys. */
  onSetGroups: (keys: string[], open: boolean) => void;
}

interface WeekGridRowProps extends GridRowHandlers {
  row: GridRow;
  today: IsoDate;
  /** Under a project heading. */
  grouped?: boolean;
  /** The heading shows this row's guidance. */
  planOnHeading?: boolean;
  /** The row's place among the grid's task rows, for keyboard movement. */
  gridRow: number;
}

/** One task row of the desktop grid. */
export function WeekGridRow({
  row,
  today,
  pending,
  resets,
  saving,
  favoritePending,
  onToggleFavorite,
  onCommit,
  onOpenEntries,
  grouped = false,
  planOnHeading = false,
  gridRow,
}: WeekGridRowProps) {
  const held = heldInRow(row, pending);
  return (
    <TableRow>
      <TableCell className="align-top">
        <GridTaskInfo
          row={row}
          held={held}
          favoritePending={favoritePending}
          onToggleFavorite={() => onToggleFavorite(row)}
          grouped={grouped}
          planOnHeading={planOnHeading}
        />
        <HeldComments
          held={held}
          saving={saving}
          idPrefix=""
          onSave={(x, comment) => onCommit(row, x.cell, x.p.raw, comment)}
        />
      </TableCell>
      {row.cells.map((cell, col) => {
        const p = pending[cell.key];
        return (
          <TableCell
            key={cell.key}
            className={cn("text-center align-top", cell.date === today && TODAY_TINT)}
          >
            <GridCell
              key={cellKey(cell, p, resets[cell.key] ?? 0)}
              cell={cell}
              row={gridRow}
              col={col}
              label={cellLabel(row, cell.date)}
              pendingRaw={p ? p.raw : null}
              onCommit={(raw) => onCommit(row, cell, raw, null)}
              onOpenEntries={() => onOpenEntries(row, cell)}
            />
          </TableCell>
        );
      })}
      <TableCell className="text-right font-semibold align-top leading-11 md:leading-9">
        {row.weekHours > 0 ? formatHours(row.weekHours) : "–"}
      </TableCell>
    </TableRow>
  );
}

/** "CP - App Dev (Crye Precision)": a project heading's name in its control's label. */
export function projectGroupName(group: GridGroup): string {
  return group.accountName ? `${group.projectName} (${group.accountName})` : group.projectName;
}

interface WeekGridGroupRowProps {
  group: GridGroup;
  open: boolean;
  days: IsoDate[];
  today: IsoDate;
  onToggle: () => void;
}

/** A project's heading row in the desktop grid: collapse it, its guidance and its totals. */
export function WeekGridGroupRow({ group, open, days, today, onToggle }: WeekGridGroupRowProps) {
  const count = group.rows.length;
  return (
    <TableRow className="bg-muted/50 hover:bg-muted/50">
      <TableCell className="py-1">
        <Button
          variant="ghost"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Expand"} ${projectGroupName(group)}`}
          className={cn(
            TOUCH,
            "h-auto w-full justify-start whitespace-normal text-left font-normal",
          )}
        >
          <ChevronRight
            className={cn(
              "size-4 shrink-0 text-muted-foreground transition-transform",
              open && "rotate-90",
            )}
            aria-hidden="true"
          />
          <span className="min-w-0">
            <span className="block font-display text-sm font-bold break-words">
              {group.projectName}
            </span>
            <span className="block text-xs text-muted-foreground">
              {[group.accountName, `${count} ${count === 1 ? "task" : "tasks"}`]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
        </Button>
        {group.plan && <GroupPlan plan={group.plan} />}
      </TableCell>
      {days.map((day, i) => (
        <TableCell
          key={day}
          className={cn(
            "text-center text-sm font-semibold text-muted-foreground",
            day === today && TODAY_TINT,
          )}
        >
          {(group.dayTotals[i] ?? 0) > 0 ? formatHours(group.dayTotals[i] ?? 0) : ""}
        </TableCell>
      ))}
      <TableCell className="text-right font-semibold">
        {group.weekHours > 0 ? formatHours(group.weekHours) : "–"}
      </TableCell>
    </TableRow>
  );
}
